import React, { useEffect, useState } from "react";

import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  Container,
  Grid2 as Grid,
  Grid2Props as GridProps,
} from "@mui/material";

import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  NumberInput,
  Pagination,
  Select,
  Spoiler,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Divider,
  Table,
} from "@mantine/core";
import { Dropzone, IMAGE_MIME_TYPE, MIME_TYPES } from "@mantine/dropzone";

import { useDisclosure } from "@mantine/hooks";
import {
  IconCalendar,
  IconCheck,
  IconChevronRight,
  IconEdit,
  IconEye,
  IconNotebook,
  IconPlus,
  IconReload,
  IconUserCircle,
  IconUsers,
  IconX,
  IconVideo,
  IconUpload,
  IconPhoto,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import DisplayAlert from "../../Components/DisplayAlert";
import MegaLoader from "../../Components/MegaLoader";
import { Colors, HttpErrorCodes, HttpSuccessCodesObj } from "../../Lib/data";
import { Endpoints } from "../../Lib/endpoints";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  getEllipsisFromString,
  getPaginationTotal,
} from "../../Lib/methods";
import { NonPaginatedResponse, PaginatedResponse } from "../../Lib/responses";
import {
  BatchProps,
  CourseProps,
  LessonProps,
  PaginationProps,
  RecordingProps,
  StudentProfileProps,
  TeacherProps,
} from "../../Lib/types";
import {
  PerformRequest,
  UploadFiles,
  usePerformRequest,
} from "../../Lib/usePerformRequest";
import "./styles.scss";
import { useForm } from "@mantine/form";
import { GridRenderCellParams } from "@mui/x-data-grid";
import DatagridDisplay from "../../Components/DatagridDisplay";
import { DateTimePicker } from "@mantine/dates";

const SingleCourse: React.FC = () => {
  const { courseID } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [studentsPagination, setStudentsPagination] = useState<PaginationProps>(
    {
      page: 1,
      limit: 30,
    }
  );
  const {
    data: course,
    isLoading: isLoadingCourse,
    reloadData: reloadCourse,
    response: courseAxiosResponse,
  } = usePerformRequest<NonPaginatedResponse<CourseProps>>({
    route: Endpoints.GetSingleCourseDetails(courseID as string).route,
    method: Endpoints.GetSingleCourseDetails(courseID as string).method,
  });
  const [batchesPagination, setBatchesPagination] = useState<PaginationProps>({
    page: 1,
    limit: 100,
  });

  const {
    data: batches,
    isLoading: isLoadingBatches,
    reloadData: reloadBatches,
    response: batchesAxiosResponse,
  } = usePerformRequest<PaginatedResponse<BatchProps[]>>({
    route: Endpoints.GetCourseBatches(
      courseID as string,
      batchesPagination.page,
      batchesPagination.limit
    ).route,
    method: Endpoints.GetCourseBatches(
      courseID as string,
      batchesPagination.page,
      batchesPagination.limit
    ).method,
  });
  const {
    data: students,
    isLoading: isLoadingStudents,
    reloadData: reloadStudents,
    response: studentsAxiosResponse,
  } = usePerformRequest<PaginatedResponse<StudentProfileProps[]>>({
    method: Endpoints.GetStudentsEnrolledInCourse(courseID as string).method,
    route: Endpoints.GetStudentsEnrolledInCourse(courseID as string).route,
    body: {
      page: studentsPagination.page,
      limit: studentsPagination.limit,
    },
  });

  const {
    data: teachers,
    isLoading: isLoadingTeachers,
    reloadData: reloadTeachers,
    error: teachersError,
    response: teachersAxiosResponse,
  } = usePerformRequest<NonPaginatedResponse<TeacherProps[]>>({
    route: Endpoints.GetTeachers(1, 1000).route,
    method: Endpoints.GetTeachers(1, 1000).method,
  });

  const getTeacherByID = (teacherID: string) => {
    const teacher = teachers?.data?.find((t) => t.id === teacherID);
    if (teacher && teacher.id) {
      return teacher;
    } else {
      return undefined;
    }
  };

  const [isEditTeacherOpen, setEditTeacherOpen] = useState<boolean>(false);
  const [selectedTeacher, setSelectedTeacher] = useState<string | null>(null);

  const [isUpdatingCourseTeacher, setIsUpdatingCourseTeacher] =
    useState<boolean>(false);
  const UpdateCourseTeacher = async () => {
    if (
      selectedTeacher &&
      course &&
      course.data &&
      selectedTeacher.length > 0
    ) {
      setIsUpdatingCourseTeacher(true);
      const r = await PerformRequest({
        route: Endpoints.GetAssignTeacherToCourse().route,
        method: Endpoints.GetAssignTeacherToCourse().method,
        data: {
          courseID: course.data.id,
          teacherID: selectedTeacher,
        },
        callback: () => {
          setIsUpdatingCourseTeacher(false);
        },
      });
      if (r) {
        if (r.status === 200) {
          setIsUpdatingCourseTeacher(false);
          setEditTeacherOpen(false);
          reloadCourse();
        } else {
          DefaultErrorNotification("Could not update course teacher!");
        }
      } else {
        DefaultErrorNotification("An error occurred!");
      }
    }
  };

  const CancelUpdateCourseTeacher = () => {
    setEditTeacherOpen(false);
    if (
      course &&
      course.data &&
      course.data.teacherID &&
      course.data.teacherID.length > 0
    ) {
      setSelectedTeacher(course.data.teacherID);
    } else {
      setSelectedTeacher(null);
    }
  };
  const [lessonsPagination, setLessonsPagination] = useState<PaginationProps>({
    page: 1,
    limit: 100,
  });
  const {
    data: lessons,
    isLoading: isLoadingLessons,
    reloadData: reloadLessons,
    response: lessonsAxiosResponse,
  } = usePerformRequest<PaginatedResponse<LessonProps[]>>({
    route: Endpoints.GetLessonsUnderCourse(
      courseID as string,
      lessonsPagination.page,
      lessonsPagination.limit
    ).route,
    method: Endpoints.GetLessonsUnderCourse(
      courseID as string,
      lessonsPagination.page,
      lessonsPagination.limit
    ).method,
  });
  console.log(lessons);
  const [recordingsPagination, setRecordingsPagination] =
    useState<PaginationProps>({
      page: 1,
      limit: 20,
    });
  const {
    data: recordings,
    isLoading: isLoadingRecordings,
    reloadData: reloadRecordings,
    response: recordingsAxiosResponse,
  } = usePerformRequest<PaginatedResponse<RecordingProps[]>>({
    route: Endpoints.GetRecordingsUnderCourse(
      courseID as string,
      recordingsPagination.page,
      recordingsPagination.limit
    ).route,
    method: Endpoints.GetLessonsUnderCourse(
      courseID as string,
      lessonsPagination.page,
      lessonsPagination.limit
    ).method,
  });

  const [modalFormAction, setModalFormAction] = useState<
    "create" | "update" | "view" | "join"
  >("create");
  const [
    isLessonModalOpen,
    { close: CloseLessonModal, open: OpenLessonModal },
  ] = useDisclosure(false);

  const [currentLesson, setCurrentLesson] = useState<LessonProps>();
  const [selectedBatch, setSelectedBatch] = useState<BatchProps | null>(null);
  const [isBatchModalOpen, setIsBatchModalOpen] = useState<boolean>(false);
  const [batchName, setBatchName] = useState<string>("");
  const [batchTeacher, setBatchTeacher] = useState<string | null>(null);
  const [batchMaxStudents, setBatchMaxStudents] = useState<number>(5);
  const [isUpdatingBatchTeacher, setIsUpdatingBatchTeacher] =
    useState<boolean>(false);
  const [isCreateBatchModalOpen, setIsCreateBatchModalOpen] =
    useState<boolean>(false);
  const [isCreatingBatch, setIsCreatingBatch] = useState<boolean>(false);
  const [isTransferStudentModalOpen, setIsTransferStudentModalOpen] =
    useState<boolean>(false);
  const [studentToTransfer, setStudentToTransfer] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [targetBatchID, setTargetBatchID] = useState<string | null>(null);
  const [isTransferringStudent, setIsTransferringStudent] =
    useState<boolean>(false);
  const [isRecordingModalOpen, setIsRecordingModalOpen] =
    useState<boolean>(false);
  const [selectedRecordingFiles, setSelectedRecordingFiles] = useState<File[]>(
    []
  );
  const [selectedLessonForRecording, setSelectedLessonForRecording] = useState<
    string | null
  >(null);
  const [isCreatingRecording, setCreatingRecording] = useState<boolean>(false);
  const [fileRejectionErrors, setFileRejectionErrors] = useState<string[]>([]);
  const [selectedRecording, setSelectedRecording] =
    useState<RecordingProps | null>(null);
  const [isRecordingDetailsModalOpen, setIsRecordingDetailsModalOpen] =
    useState<boolean>(false);
  useEffect(() => {
    if (courseAxiosResponse) {
      if (HttpErrorCodes.includes(courseAxiosResponse.status)) {
        DefaultErrorNotification("Could not get course details!");
        navigate("/d/courses");
      }
    }
    if (course && course.data) {
      setSelectedTeacher(course.data.teacherID);
    }
  }, [isLoadingCourse, courseAxiosResponse]);

  const lessonForm = useForm({
    initialValues: {
      title: "",
      description: "",
      initialRunTime: new Date(),
      batchID: "",
    },
    validate: {
      title: (value) =>
        value && value.length > 5 ? null : "Title is required",
      description: (value) =>
        value && value.length > 5 ? null : "Description is required",
      initialRunTime: (value) => (value ? null : "Lesson date is required"),
      batchID: (value) => (value && value.length > 0 ? null : "Batch is required"),
    },
  });
  const [isCreatingOrUpdatingLesson, setIsCreatingOrUpdatingLesson] =
    useState<boolean>(false);
  const createBatchForm = useForm({
    initialValues: {
      name: "",
      maxStudents: 5,
      teacherID: "",
    },
    validate: {
      name: (value) =>
        !value || value.trim().length >= 2
          ? null
          : "Batch name must be at least 2 characters",
      maxStudents: (value) =>
        typeof value === "number" && value >= 1
          ? null
          : "Max students must be at least 1",
    },
  });

  const CreateNewLesson = async () => {
    const { hasErrors } = lessonForm.validate();

    if (!hasErrors) {
      const { title, description, initialRunTime, batchID } = lessonForm.values;
      setIsCreatingOrUpdatingLesson(true);
      const r = await PerformRequest({
        route: Endpoints.GetCreateLesson(courseID as string).route,
        method: Endpoints.GetCreateLesson(courseID as string).method,
        data: {
          title,
          description,
          batchID,
          initialRunTime: new Date(initialRunTime).valueOf().toString(),
        },
        callback: () => {
          setIsCreatingOrUpdatingLesson(false);
        },
      });
      setIsCreatingOrUpdatingLesson(false);
      if (r) {
        if (r.status === 200) {
          lessonForm.reset();
          reloadLessons();
          CloseLessonModal();
        } else {
          DefaultErrorNotification("Could not create lesson!");
        }
      } else {
        DefaultErrorNotification("An error occurred!");
      }
    }
  };

  const UpdateLesson = async () => {
    const { hasErrors } = lessonForm.validate();
    if (!hasErrors) {
      const { title, description, initialRunTime, batchID } = lessonForm.values;
      setIsCreatingOrUpdatingLesson(true);
      const r = await PerformRequest({
        route: Endpoints.GetUpdateLesson(currentLesson?.id as string).route,
        method: Endpoints.GetUpdateLesson(currentLesson?.id as string).method,
        data: {
          title,
          description,
          batchID,
          initialRunTime: new Date(initialRunTime).valueOf().toString(),
        },
        callback: () => {
          setIsCreatingOrUpdatingLesson(false);
        },
      });
      setIsCreatingOrUpdatingLesson(false);
      if (r) {
        if (r.status === HttpSuccessCodesObj.Success) {
          lessonForm.reset();
          reloadLessons();
          CloseLessonModal();
          setCurrentLesson(undefined);
          DefaultSuccessNotification("Lesson updated successfully!");
        } else {
          DefaultErrorNotification("Could not update lesson!");
        }
      } else {
        DefaultErrorNotification("An error occurred!");
      }
    }
  };

  const [isJoiningLesson, setIsJoiningLesson] = useState<boolean>(false);
  const [selectedLessonBatchFilter, setSelectedLessonBatchFilter] = useState<
    string | null
  >(null);

  const ShowLessonBatchesToJoinRoom = async (lessonID: string) => {
    setModalFormAction("join");
    const lesson = lessons?.data?.find((l) => l.id === lessonID);
    if (lesson) {
      setCurrentLesson(lesson);
    }
  };

  const getBatchLabel = (batchID?: string) => {
    if (!batchID) return "No batch assigned";
    return batches?.data?.find((batch) => batch.id === batchID)?.name || "Unknown batch";
  };

  const filteredLessons = lessons?.data?.filter((lesson) => {
    if (!selectedLessonBatchFilter) return true;
    return lesson.batchID === selectedLessonBatchFilter;
  });

  const handleBatchClick = (batch: BatchProps) => {
    setSelectedBatch(batch);
    setBatchName(batch.name || "");
    setBatchTeacher(batch.teacherID);
    setBatchMaxStudents(batch.maxStudents || 5);
    setIsBatchModalOpen(true);
  };

  const UpdateBatchTeacher = async () => {
    if (!selectedBatch || batchMaxStudents < 1) return;

    setIsUpdatingBatchTeacher(true);
    try {
      const r = await PerformRequest({
        route: Endpoints.GetUpdateBatch(selectedBatch.id).route,
        method: Endpoints.GetUpdateBatch(selectedBatch.id).method,
        data: {
          name: batchName || undefined,
          teacherID: batchTeacher || undefined,
          maxStudents: batchMaxStudents,
        },
        callback: () => setIsUpdatingBatchTeacher(false),
      });
      setIsUpdatingBatchTeacher(false);
      if (r && r.status === HttpSuccessCodesObj.Success) {
        DefaultSuccessNotification("Batch teacher updated successfully!");
        setIsBatchModalOpen(false);
        setSelectedBatch(null);
        setBatchName("");
        setBatchTeacher(null);
        setBatchMaxStudents(5);
        reloadBatches();
      } else {
        DefaultErrorNotification(
          r?.message || "Failed to update batch teacher"
        );
      }
    } catch (error) {
      DefaultErrorNotification(
        "An error occurred while updating batch teacher"
      );
    } finally {
      setIsUpdatingBatchTeacher(false);
    }
  };

  const [isRemovingBatchTeacher, setRemovingBatchTeacher] =
    useState<boolean>(false);
  const RemoveBatchTeacher = async () => {
    if (!selectedBatch) return;
    setRemovingBatchTeacher(true);

    const r = await PerformRequest({
      route: Endpoints.GetRemoveBatchTeacher(selectedBatch.id).route,
      method: Endpoints.GetRemoveBatchTeacher(selectedBatch.id).method,
      callback: () => setRemovingBatchTeacher(false),
    });
    setRemovingBatchTeacher(false);
    if (r) {
      if (r.status === HttpSuccessCodesObj.Success) {
        DefaultSuccessNotification("Batch teacher removed successfully!");
        setIsBatchModalOpen(false);
        setSelectedBatch(null);
        setBatchName("");
        setBatchTeacher(null);
        setBatchMaxStudents(5);
        reloadBatches();
      } else {
        DefaultErrorNotification(
          r?.message || "Failed to remove batch teacher"
        );
      }
    } else {
      DefaultErrorNotification("An error occurred!");
    }
  };

  const handleCloseBatchModal = () => {
    setIsBatchModalOpen(false);
    setSelectedBatch(null);
    setBatchName("");
    setBatchTeacher(null);
    setBatchMaxStudents(5);
  };

  useEffect(() => {
    if (selectedBatch && batches?.data) {
      const freshBatch = batches.data.find((batch) => batch.id === selectedBatch.id);
      if (freshBatch) {
        setSelectedBatch(freshBatch);
        setBatchName(freshBatch.name || "");
        setBatchTeacher(freshBatch.teacherID);
        setBatchMaxStudents(freshBatch.maxStudents || 5);
      }
    }
  }, [batches?.data, selectedBatch?.id]);

  const CreateBatch = async () => {
    const { hasErrors } = createBatchForm.validate();
    if (hasErrors || !courseID) return;

    setIsCreatingBatch(true);
    const r = await PerformRequest({
      route: Endpoints.GetCreateBatch().route,
      method: Endpoints.GetCreateBatch().method,
      data: {
        courseID,
        name: createBatchForm.values.name || undefined,
        maxStudents: createBatchForm.values.maxStudents,
        teacherID: createBatchForm.values.teacherID || undefined,
      },
      callback: () => setIsCreatingBatch(false),
    });
    setIsCreatingBatch(false);

    if (r?.status === HttpSuccessCodesObj.Created) {
      DefaultSuccessNotification("Batch created successfully!");
      createBatchForm.reset();
      createBatchForm.setFieldValue("name", "");
      createBatchForm.setFieldValue("maxStudents", 5);
      setIsCreateBatchModalOpen(false);
      reloadBatches();
    } else {
      DefaultErrorNotification(r?.message || "Failed to create batch");
    }
  };

  const OpenTransferStudentModal = (student: { id: string; name: string }) => {
    setStudentToTransfer(student);
    setTargetBatchID(null);
    setIsTransferStudentModalOpen(true);
  };

  const TransferStudent = async () => {
    if (!selectedBatch || !studentToTransfer || !targetBatchID) return;

    setIsTransferringStudent(true);
    const r = await PerformRequest({
      route: Endpoints.GetTransferStudentBetweenBatches(selectedBatch.id).route,
      method: Endpoints.GetTransferStudentBetweenBatches(selectedBatch.id).method,
      data: {
        studentID: studentToTransfer.id,
        targetBatchID,
      },
      callback: () => setIsTransferringStudent(false),
    });
    setIsTransferringStudent(false);

    if (r?.status === HttpSuccessCodesObj.Success) {
      DefaultSuccessNotification("Student transferred successfully!");
      setIsTransferStudentModalOpen(false);
      setStudentToTransfer(null);
      setTargetBatchID(null);
      reloadBatches();
    } else {
      DefaultErrorNotification(r?.message || "Failed to transfer student");
    }
  };

  const handleRecordingFileDrop = (files: File[]) => {
    setSelectedRecordingFiles(files);
    setFileRejectionErrors([]); // Clear any previous errors when valid files are dropped
  };

  const handleRecordingFileReject = (fileRejections: any[]) => {
    const errors: string[] = [];

    fileRejections.forEach((rejection) => {
      rejection.errors.forEach((error: any) => {
        switch (error.code) {
          case "file-invalid-type":
            errors.push(
              `File "${rejection.file.name}" has an invalid format. Please upload MP4 files only.`
            );
            break;
          case "file-too-large":
            errors.push(
              `File "${rejection.file.name}" is too large. Maximum file size is 1GB.`
            );
            break;
          case "too-many-files":
            errors.push(`Too many files selected. Maximum 5 files allowed.`);
            break;
          default:
            errors.push(
              `File "${rejection.file.name}" was rejected: ${error.message}`
            );
        }
      });
    });

    setFileRejectionErrors(errors);
    DefaultErrorNotification(errors.join(" "));
  };

  const CreateNewRecording = async () => {
    if (!selectedLessonForRecording || selectedRecordingFiles.length === 0) {
      DefaultErrorNotification(
        "Please select a lesson and upload recording files"
      );
      return;
    } else {
      setCreatingRecording(true);
      try {
        const uploaded = await UploadFiles(selectedRecordingFiles, true);
        setCreatingRecording(false);
        if (uploaded && uploaded.status === HttpSuccessCodesObj.Created) {
          setCreatingRecording(true);
          const r = await PerformRequest({
            route: Endpoints.GetCreateRecording().route,
            method: Endpoints.GetCreateRecording().method,
            data: {
              courseID: courseID as string,
              lessonID: selectedLessonForRecording,
              files: uploaded.data.map((f) => f.path),
            },
            callback: () => setCreatingRecording(false),
          });
          setCreatingRecording(false);
          if (r) {
            if (r.status === HttpSuccessCodesObj.Created) {
              DefaultSuccessNotification("Recording created successfully!");
              reloadRecordings();
              setSelectedRecordingFiles([]);
              setSelectedLessonForRecording(null);
              setIsRecordingModalOpen(false);
            } else {
              DefaultErrorNotification("Failed to create recording");
            }
          }
        } else {
          setCreatingRecording(false);
          DefaultErrorNotification("Failed to upload recording files");
        }
      } catch (error) {
        setCreatingRecording(false);
        DefaultErrorNotification("Failed to create recording");
      }
    }
  };

  const handleCloseRecordingModal = () => {
    setIsRecordingModalOpen(false);
    setSelectedRecordingFiles([]);
    setSelectedLessonForRecording(null);
    setFileRejectionErrors([]);
  };

  const handleRecordingClick = (recording: RecordingProps) => {
    setSelectedRecording(recording);
    setIsRecordingDetailsModalOpen(true);
  };

  const handleCloseRecordingDetailsModal = () => {
    setIsRecordingDetailsModalOpen(false);
    setSelectedRecording(null);
  };

  const handleFileClick = (filePath: string) => {
    window.open(filePath, "_blank");
  };

  const gridProps: GridProps = {
    size: {
      xs: 12,
      sm: 6,
      md: 4,
      lg: 4,
      xl: 4,
    },
  };

  return (
    <Container maxWidth="lg">
      <div className="single-course-container">
        {isLoadingCourse ? (
          <MegaLoader />
        ) : (
          <>
            <Text fw={700} fz={21} c={Colors.PurplePrimary}>
              {course?.data.name}
            </Text>
            <Text mb={50} mt={10} maw={700} fz={16}>
              <Spoiler maxHeight={50} showLabel="Show more" hideLabel="Hide">
                {/* Content here */}
                {course?.data.description}
              </Spoiler>
            </Text>
            {isEditTeacherOpen ? (
              <div className="flex-row align-center">
                <Select
                  maw={240}
                  spellCheck={false}
                  allowDeselect={false}
                  searchable
                  value={selectedTeacher}
                  onChange={(value) => setSelectedTeacher(value)}
                  data={teachers?.data?.map((teacher) => ({
                    value: teacher.id,
                    label: teacher.name,
                  }))}
                  placeholder="Select teacher"
                />
                <ActionIcon
                  size="lg"
                  color={Colors.PurplePrimary}
                  ml={5}
                  disabled={
                    !selectedTeacher ||
                    selectedTeacher.length === 0 ||
                    course?.data?.teacherID === selectedTeacher
                  }
                  variant="outline"
                  onClick={UpdateCourseTeacher}
                  loading={isUpdatingCourseTeacher}
                >
                  <IconCheck size={17} />
                </ActionIcon>
                <ActionIcon
                  size="lg"
                  color={Colors.OrangePrimary}
                  ml={5}
                  variant="outline"
                  disabled={isUpdatingCourseTeacher}
                  onClick={CancelUpdateCourseTeacher}
                >
                  <IconX size={17} />
                </ActionIcon>
              </div>
            ) : (
              <div className="flex-row align-center">
                <IconUserCircle
                  color={
                    course?.data?.teacherID &&
                    course?.data?.teacherID?.length > 0
                      ? Colors.BluePrimary
                      : Colors.GraySecondary
                  }
                />
                {course?.data?.teacherID &&
                course?.data?.teacherID?.length > 0 ? (
                  <Text fz={16} c={Colors.BluePrimary}>
                    {getTeacherByID(course?.data?.teacherID)?.name}
                  </Text>
                ) : (
                  <Text
                    fz={15}
                    c={Colors.OrangePrimary}
                    style={{
                      textDecoration: "underline",
                      textDecorationColor: Colors.OrangePrimary,
                      textDecorationThickness: 1,
                      textUnderlineOffset: 4,
                      textDecorationStyle: "dashed",
                    }}
                  >
                    No teacher assigned
                  </Text>
                )}
                <ActionIcon
                  ml={5}
                  variant="outline"
                  onClick={() => setEditTeacherOpen(true)}
                >
                  <IconEdit size={17} />
                </ActionIcon>
              </div>
            )}
            <Tabs
              mt={25}
              defaultValue="lessons"
              variant="default"
              color={Colors.PurplePrimary}
            >
              <Tabs.List grow>
                <Tabs.Tab
                  value="lessons"
                  leftSection={<IconNotebook size={19} />}
                >
                  Lessons
                </Tabs.Tab>
                <Tabs.Tab
                  value="students"
                  leftSection={<IconUsers size={19} />}
                >
                  Students
                </Tabs.Tab>
                <Tabs.Tab
                  value="batches"
                  leftSection={<IconCalendar size={19} />}
                >
                  Batches
                </Tabs.Tab>
                <Tabs.Tab
                  value="recordings"
                  leftSection={<IconVideo size={19} />}
                >
                  Recordings
                </Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="lessons">
                <div className="flex-row align-center width-100 justify-between">
                  <div className="flex-row align-center">
                    <IconChevronRight size={19} color={Colors.PurplePrimary} />
                    <Text fw={600} my={10} c={Colors.PurplePrimary}>
                      Lessons
                    </Text>
                  </div>
                  <div className="flex-row align-center">
                    <ActionIcon
                      size="lg"
                      color={Colors.PurplePrimary}
                      onClick={() => {
                        OpenLessonModal();
                        setModalFormAction("create");
                        lessonForm.reset();
                      }}
                    >
                      <IconPlus size={18} />
                    </ActionIcon>
                    <ActionIcon
                      size="lg"
                      color={Colors.PurplePrimary}
                      variant="outline"
                      onClick={reloadLessons}
                      loading={isLoadingCourse || isLoadingLessons}
                      ml={10}
                      disabled={isCreatingOrUpdatingLesson}
                    >
                      <IconReload size={18} />
                    </ActionIcon>
                  </div>
                </div>
                <Group mb="md" justify="space-between">
                  <Select
                    maw={260}
                    searchable
                    clearable
                    label="Filter by batch"
                    placeholder="All batches"
                    value={selectedLessonBatchFilter}
                    onChange={setSelectedLessonBatchFilter}
                    data={
                      batches?.data?.map((batch) => ({
                        value: batch.id,
                        label: batch.name || `Batch ${batch.id}`,
                      })) || []
                    }
                  />
                </Group>
                <div className="flex-col width-100">
                  {isLoadingLessons && <MegaLoader />}

                  {!isLoadingLessons && (
                    <>
                      {filteredLessons && filteredLessons.length > 0 ? (
                        <>
                          <Grid container spacing={2}>
                            {filteredLessons.map((lesson) => (
                              <Grid {...gridProps} key={lesson.id}>
                                <Card
                                  key={lesson.id}
                                  shadow="sm"
                                  padding="lg"
                                  radius="md"
                                  withBorder
                                  mt="sm"
                                  h={250}
                                  className="lesson-card"
                                >
                                  <Group justify="space-between" mb="xs">
                                    <Text
                                      fw={500}
                                      fz={15}
                                      c={Colors.PurplePrimary}
                                    >
                                      {lesson.title}
                                    </Text>
                                    <Group gap="xs">
                                      <Badge color="blue" variant="light">
                                        {lesson.batchName || getBatchLabel(lesson.batchID)}
                                      </Badge>
                                      <Badge
                                        color={Colors.PurplePrimary}
                                        variant="light"
                                      >
                                        {dayjs(lesson.initialRunTime).format(
                                          "DD/MM/YYYY"
                                        )}
                                      </Badge>
                                    </Group>
                                  </Group>

                                  <Text size="sm" c={Colors.GrayPrimary}>
                                    {getEllipsisFromString(
                                      lesson?.description ?? "",
                                      150
                                    )}
                                  </Text>

                                  <Button
                                    variant="outline"
                                    color={Colors.PurplePrimary}
                                    fullWidth
                                    mt="md"
                                    radius="sm"
                                    leftSection={<IconEye size={16} />}
                                    onClick={() => {
                                      OpenLessonModal();
                                      setModalFormAction("view");
                                      setCurrentLesson(lesson);
                                    }}
                                  >
                                    View Details
                                  </Button>
                                </Card>
                              </Grid>
                            ))}
                          </Grid>
                          <div className="flex-row align-center justify-center width-100">
                            <Pagination
                              value={lessonsPagination.page}
                              onChange={(value) => {
                                setLessonsPagination({
                                  ...lessonsPagination,
                                  page: value,
                                });
                              }}
                              my={40}
                              disabled={
                                lessons?.data.length === 0 || isLoadingLessons
                              }
                              total={getPaginationTotal(
                                lessons?.totalRecords,
                                lessonsPagination.limit
                              )}
                            />
                          </div>
                        </>
                      ) : (
                        <DisplayAlert
                          title="No lessons found"
                          children={
                            <div className="flex-col">
                              <Text>
                                No lessons found for this course. You can start
                                creating lessons by clicking the button below.
                              </Text>
                              <Button
                                w={160}
                                mt={10}
                                color={Colors.PurplePrimary}
                                leftSection={<IconPlus size={16} />}
                                onClick={() => {
                                  OpenLessonModal();
                                  setModalFormAction("create");
                                }}
                              >
                                Create Lesson
                              </Button>
                            </div>
                          }
                        />
                      )}
                    </>
                  )}
                </div>
              </Tabs.Panel>
              <Tabs.Panel value="students">
                <div className="flex-row align-center width-100 justify-between">
                  <div className="flex-row align-center">
                    <IconChevronRight size={19} color={Colors.PurplePrimary} />
                    <Text fw={600} my={10} c={Colors.PurplePrimary}>
                      Students
                    </Text>
                  </div>
                  <ActionIcon
                    size="lg"
                    color={Colors.PurplePrimary}
                    variant="outline"
                    onClick={reloadStudents}
                    loading={isLoadingStudents}
                    ml={10}
                  >
                    <IconReload size={18} />
                  </ActionIcon>
                </div>
                <div className="flex-col width-100">
                  {isLoadingStudents ? (
                    <MegaLoader />
                  ) : !students?.data || students.data.length === 0 ? (
                    <DisplayAlert
                      title="No Students Found"
                      children={
                        <Text>
                          No students are currently enrolled in this course.
                        </Text>
                      }
                    />
                  ) : (
                    <DatagridDisplay
                      rows={students.data}
                      isLoading={isLoadingStudents}
                      columns={[
                        {
                          field: "name",
                          flex: 1,
                          headerName: "Name",
                          renderCell: (
                            params: GridRenderCellParams<StudentProfileProps>
                          ) => (
                            <div className="flex-row flex-1 align-center">
                              <Text fz={13} className="table-value">
                                {params.row.name}
                              </Text>
                            </div>
                          ),
                        },
                        {
                          field: "email",
                          flex: 1,
                          headerName: "Email",
                          renderCell: (
                            params: GridRenderCellParams<StudentProfileProps>
                          ) => (
                            <div className="flex-row flex-1 align-center">
                              <Text fz={13} className="table-value">
                                {params.row.email}
                              </Text>
                            </div>
                          ),
                        },
                        {
                          field: "isEmailVerified",
                          flex: 1,
                          headerName: "Email Verified",
                          renderCell: (
                            params: GridRenderCellParams<StudentProfileProps>
                          ) => (
                            <div className="flex-row flex-1 align-center">
                              <Badge
                                color={
                                  params.row.isEmailVerified ? "green" : "red"
                                }
                                variant="light"
                              >
                                {params.row.isEmailVerified ? "Yes" : "No"}
                              </Badge>
                            </div>
                          ),
                        },
                        {
                          field: "isActive",
                          flex: 1,
                          headerName: "Status",
                          renderCell: (
                            params: GridRenderCellParams<StudentProfileProps>
                          ) => (
                            <div className="flex-row flex-1 align-center">
                              <Badge
                                color={params.row.isActive ? "green" : "red"}
                                variant="light"
                              >
                                {params.row.isActive ? "Active" : "Inactive"}
                              </Badge>
                            </div>
                          ),
                        },
                      ]}
                    />
                  )}
                  {students && students.data && students.data.length > 0 && (
                    <div className="flex-row align-center justify-center width-100">
                      <Pagination
                        value={studentsPagination.page}
                        onChange={(value) => {
                          setStudentsPagination({
                            ...studentsPagination,
                            page: value,
                          });
                        }}
                        my={40}
                        disabled={
                          students?.data.length === 0 || isLoadingStudents
                        }
                        total={getPaginationTotal(
                          students?.totalRecords,
                          studentsPagination.limit
                        )}
                      />
                    </div>
                  )}
                </div>
              </Tabs.Panel>
              <Tabs.Panel value="batches">
                <div className="flex-row align-center width-100 justify-between">
                  <div className="flex-row align-center">
                    <IconChevronRight size={19} color={Colors.PurplePrimary} />
                    <Text fw={600} my={10} c={Colors.PurplePrimary}>
                      Batches
                    </Text>
                  </div>
                  <Group gap="sm">
                    <Button
                      color={Colors.PurplePrimary}
                      leftSection={<IconPlus size={16} />}
                      onClick={() => setIsCreateBatchModalOpen(true)}
                    >
                      Create Batch
                    </Button>
                    <ActionIcon
                      size="lg"
                      color={Colors.PurplePrimary}
                      variant="outline"
                      onClick={reloadBatches}
                      loading={isLoadingBatches}
                      ml={10}
                    >
                      <IconReload size={18} />
                    </ActionIcon>
                  </Group>
                </div>
                <div className="flex-col width-100">
                  {isLoadingBatches ? (
                    <MegaLoader />
                  ) : !batches?.data || batches.data.length === 0 ? (
                    <DisplayAlert
                      title="No Batches Found"
                      children={
                        <Text>
                          No batches are currently available for this course.
                        </Text>
                      }
                    />
                  ) : (
                    <DatagridDisplay
                      rows={batches.data}
                      isLoading={isLoadingBatches}
                      onRowClick={(params) => handleBatchClick(params.row)}
                      columns={[
                        {
                          field: "name",
                          flex: 1,
                          headerName: "Batch",
                          renderCell: (
                            params: GridRenderCellParams<BatchProps>
                          ) => (
                            <div className="flex-row flex-1 align-center">
                              <Text fz={13} className="table-value">
                                {params.row.name || `Batch ${batches.data.indexOf(params.row) + 1}`}
                              </Text>
                            </div>
                          ),
                        },
                        {
                          field: "studentIDs",
                          flex: 1,
                          headerName: "Students",
                          renderCell: (
                            params: GridRenderCellParams<BatchProps>
                          ) => (
                            <div className="flex-row flex-1 align-center">
                              <Badge color="blue" variant="light" size="sm">
                                {params.row.studentIDs.length}/
                                {params.row.maxStudents || 5} students
                              </Badge>
                            </div>
                          ),
                        },
                        {
                          field: "maxStudents",
                          flex: 1,
                          headerName: "Capacity",
                          renderCell: (
                            params: GridRenderCellParams<BatchProps>
                          ) => (
                            <Text fz={13} className="table-value">
                              {params.row.maxStudents || 5}
                            </Text>
                          ),
                        },
                        {
                          field: "teacherID",
                          flex: 1,
                          headerName: "Teacher",
                          renderCell: (
                            params: GridRenderCellParams<BatchProps>
                          ) => (
                            <div className="flex-row flex-1 align-center">
                              <Text fz={13} className="table-value">
                                {getTeacherByID(params.row.teacherID)?.name}
                              </Text>
                            </div>
                          ),
                        },
                        {
                          field: "createdAt",
                          flex: 1,
                          headerName: "Created",
                          renderCell: (
                            params: GridRenderCellParams<BatchProps>
                          ) => (
                            <div className="flex-row flex-1 align-center">
                              <Text fz={13} className="table-value">
                                {dayjs(params.row.createdAt).format(
                                  "DD MMM YYYY"
                                )}
                              </Text>
                            </div>
                          ),
                        },
                        {
                          field: "lastUpdatedAt",
                          flex: 1,
                          headerName: "Last Updated",
                          renderCell: (
                            params: GridRenderCellParams<BatchProps>
                          ) => (
                            <div className="flex-row flex-1 align-center">
                              <Text fz={13} className="table-value">
                                {dayjs(params.row.lastUpdatedAt).format(
                                  "DD MMM YYYY"
                                )}
                              </Text>
                            </div>
                          ),
                        },
                      ]}
                    />
                  )}
                  {batches && batches.data && batches.data.length > 0 && (
                    <div className="flex-row align-center justify-center width-100">
                      <Pagination
                        value={batchesPagination.page}
                        onChange={(value) => {
                          setBatchesPagination({
                            ...batchesPagination,
                            page: value,
                          });
                        }}
                        my={40}
                        disabled={
                          batches?.data.length === 0 || isLoadingBatches
                        }
                        total={getPaginationTotal(
                          batches?.totalRecords,
                          batchesPagination.limit
                        )}
                      />
                    </div>
                  )}
                </div>
              </Tabs.Panel>
              <Tabs.Panel value="recordings">
                <div className="flex-row align-center width-100 justify-between">
                  <div className="flex-row align-center">
                    <IconChevronRight size={19} color={Colors.PurplePrimary} />
                    <Text fw={600} my={10} c={Colors.PurplePrimary}>
                      Recordings
                    </Text>
                  </div>
                  <div className="flex-row align-center">
                    <ActionIcon
                      size="lg"
                      color={Colors.PurplePrimary}
                      disabled={isLoadingRecordings}
                      onClick={() => setIsRecordingModalOpen(true)}
                    >
                      <IconPlus size={18} />
                    </ActionIcon>
                    <ActionIcon
                      ml={10}
                      variant="outline"
                      size="lg"
                      color={Colors.PurplePrimary}
                      onClick={reloadRecordings}
                      loading={isLoadingRecordings}
                    >
                      <IconReload size={18} />
                    </ActionIcon>
                  </div>
                </div>
                {isLoadingRecordings ? (
                  <>
                    <MegaLoader />
                  </>
                ) : (
                  <>
                    {recordings?.data && recordings.data.length > 0 ? (
                      <DatagridDisplay
                        rows={recordings.data}
                        isLoading={isLoadingRecordings}
                        onRowClick={(params) =>
                          handleRecordingClick(params.row)
                        }
                        columns={[
                          {
                            field: "lessonID",
                            flex: 1,
                            headerName: "Lesson",
                            renderCell: (
                              params: GridRenderCellParams<RecordingProps>
                            ) => (
                              <div className="flex-row flex-1 align-center">
                                <Text fz={13} className="table-value">
                                  {
                                    lessons?.data.find(
                                      (lesson) =>
                                        lesson.id === params.row.lessonID
                                    )?.title
                                  }
                                </Text>
                              </div>
                            ),
                          },
                          {
                            field: "createdAt",
                            flex: 1,
                            headerName: "Creation Date",
                            renderCell: (
                              params: GridRenderCellParams<RecordingProps>
                            ) => (
                              <div className="flex-row flex-1 align-center">
                                <Text fz={13} className="table-value">
                                  {dayjs(params.row.createdAt).format(
                                    "DD MMM YYYY"
                                  )}
                                </Text>
                              </div>
                            ),
                          },
                          {
                            field: "lastUpdatedAt",
                            flex: 1,
                            headerName: "Last Updated",
                            renderCell: (
                              params: GridRenderCellParams<RecordingProps>
                            ) => (
                              <div className="flex-row flex-1 align-center">
                                <Text fz={13} className="table-value">
                                  {dayjs(params.row.lastUpdatedAt).format(
                                    "DD MMM YYYY, hh:mm A"
                                  )}
                                </Text>
                              </div>
                            ),
                          },
                        ]}
                      />
                    ) : (
                      <div className="flex-col width-100">
                        <DisplayAlert
                          title="No Recordings Found"
                          children={
                            <div className="flex-col">
                              <Text>
                                No recordings found for this course. You can
                                start creating recordings by clicking the button
                                above.
                              </Text>
                              <Button
                                w={230}
                                mt={10}
                                color={Colors.PurplePrimary}
                                leftSection={<IconPlus size={16} />}
                                onClick={() => setIsRecordingModalOpen(true)}
                              >
                                Create Recording
                              </Button>
                            </div>
                          }
                        />
                      </div>
                    )}
                  </>
                )}
              </Tabs.Panel>
            </Tabs>
          </>
        )}
        <Modal
          centered
          size="lg"
          title={
            modalFormAction === "create"
              ? "Create Lesson"
              : modalFormAction === "update"
              ? "Update Lesson"
              : "View Lesson"
          }
          opened={isLessonModalOpen}
          onClose={() => {
            if (!isCreatingOrUpdatingLesson) {
              CloseLessonModal();
              lessonForm.reset();
            }
          }}
        >
          {modalFormAction === "create" && (
            <div className="flex-col">
              <TextInput
                placeholder="Enter lesson title"
                label="Title"
                {...lessonForm.getInputProps("title")}
              />
              <Textarea
                mt={15}
                rows={4}
                placeholder="Enter lesson description"
                label="Description"
                {...lessonForm.getInputProps("description")}
              />
              <Select
                mt={15}
                label="Batch"
                placeholder="Select batch"
                searchable
                allowDeselect={false}
                value={lessonForm.values.batchID || null}
                onChange={(value) =>
                  lessonForm.setFieldValue("batchID", value || "")
                }
                data={
                  batches?.data?.map((batch) => ({
                    value: batch.id,
                    label: batch.name || `Batch ${batch.id}`,
                  })) || []
                }
                error={lessonForm.errors.batchID}
              />
              <DateTimePicker
                mt={15}
                label="Lesson Date"
                placeholder="Select lesson date"
                {...lessonForm.getInputProps("initialRunTime")}
                // @ts-ignore
                timePickerProps={{
                  withDropdown: true,
                  popoverProps: { withinPortal: false },
                  format: "12h",
                }}
              />
              <Button
                fullWidth
                color={Colors.PurplePrimary}
                mt={20}
                loading={isCreatingOrUpdatingLesson}
                onClick={CreateNewLesson}
              >
                Create Lesson
              </Button>
            </div>
          )}
          {modalFormAction === "view" && currentLesson && (
            <div className="flex-col">
              <div className="lesson-details-header">
                <Group justify="space-between" mb={20}>
                  <div className="flex-row align-center">
                    <IconNotebook size={24} color={Colors.PurplePrimary} />
                    <Text fw={600} fz={20} ml={10} c={Colors.PurplePrimary}>
                      {currentLesson.title}
                    </Text>
                  </div>
                  <Badge color={Colors.PurplePrimary} variant="light" size="lg">
                    Created{" "}
                    {dayjs(currentLesson.createdAt).format("DD MMM YYYY")}
                  </Badge>
                </Group>
              </div>

              <Divider my="md" />

              <div className="lesson-details-content">
                <div className="lesson-metadata">
                  <Card withBorder p="md" radius="md" bg="gray.0">
                    <Group mb={15}>
                      <IconNotebook size={20} color={Colors.PurplePrimary} />
                      <Text fw={500} fz={16} c={Colors.PurplePrimary}>
                        Lesson Schedule
                      </Text>
                    </Group>
                    <Group gap="xl">
                      <div className="flex-col">
                        <Text fz={12} c="dimmed" mb={5}>
                          Batch
                        </Text>
                        <Text fw={500} fz={14}>
                          {currentLesson.batchName || getBatchLabel(currentLesson.batchID)}
                        </Text>
                      </div>
                      <div className="flex-col">
                        <Text fz={12} c="dimmed" mb={5}>
                          Scheduled Date
                        </Text>
                        <Text fw={500} fz={14}>
                          {dayjs(currentLesson.initialRunTime).format(
                            "DD MMM YYYY"
                          )}
                        </Text>
                      </div>
                      <div className="flex-col">
                        <Text fz={12} c="dimmed" mb={5}>
                          Time
                        </Text>
                        <Text fw={500} fz={14}>
                          {dayjs(currentLesson.initialRunTime).format(
                            "hh:mm A"
                          )}
                        </Text>
                      </div>
                    </Group>
                  </Card>
                </div>

                <div className="lesson-description mt={20}">
                  <Text fw={500} fz={16} mb={10} c={Colors.GrayPrimary}>
                    Description
                  </Text>
                  <Card withBorder p="md" radius="md" bg="gray.0">
                    <Text
                      fz={14}
                      c={Colors.GrayPrimary}
                      style={{ whiteSpace: "pre-wrap" }}
                    >
                      {currentLesson.description}
                    </Text>
                  </Card>
                </div>

                <Group justify="space-between" mt={30}>
                  <div className="flex-row align-center">
                    <IconEye size={18} color={Colors.GrayPrimary} />
                    <Text fz={14} c={Colors.GrayPrimary} ml={5}>
                      Last updated{" "}
                      {dayjs(currentLesson.updatedAt).format(
                        "DD MMM YYYY, hh:mm A"
                      )}
                    </Text>
                  </div>
                  <div className="flex-row align-center">
                    <Button
                      leftSection={<IconVideo size={16} />}
                      color={Colors.PurplePrimary}
                      onClick={() => {
                        ShowLessonBatchesToJoinRoom(
                          currentLesson?.id as string
                        );
                      }}
                    >
                      Join
                    </Button>
                    <Button
                      ml={10}
                      variant="outline"
                      color={Colors.PurplePrimary}
                      onClick={() => {
                        setModalFormAction("update");
                        lessonForm.setValues({
                          title: currentLesson.title,
                          description: currentLesson.description,
                          batchID: currentLesson.batchID || "",
                          initialRunTime: new Date(
                            currentLesson.initialRunTime
                          ),
                        });
                      }}
                      leftSection={<IconEdit size={16} />}
                    >
                      Edit
                    </Button>
                  </div>
                </Group>
              </div>
            </div>
          )}
          {modalFormAction === "update" && currentLesson && (
            <div className="flex-col">
              <div className="lesson-details-header">
                <Group justify="space-between" mb={20}>
                  <div className="flex-row align-center">
                    <IconEdit size={24} color={Colors.PurplePrimary} />
                    <Text fw={600} fz={20} ml={10} c={Colors.PurplePrimary}>
                      Update Lesson
                    </Text>
                  </div>
                  <Badge color={Colors.PurplePrimary} variant="light" size="lg">
                    Last updated{" "}
                    {dayjs(currentLesson.updatedAt).format("DD MMM YYYY")}
                  </Badge>
                </Group>
              </div>

              <Divider my="md" />

              <div className="lesson-details-content">
                <TextInput
                  placeholder="Enter lesson title"
                  label="Title"
                  {...lessonForm.getInputProps("title")}
                />
                <Textarea
                  mt={15}
                  rows={4}
                  placeholder="Enter lesson description"
                  label="Description"
                  {...lessonForm.getInputProps("description")}
                />
                <Select
                  mt={15}
                  label="Batch"
                  placeholder="Select batch"
                  searchable
                  allowDeselect={false}
                  value={lessonForm.values.batchID || null}
                  onChange={(value) =>
                    lessonForm.setFieldValue("batchID", value || "")
                  }
                  data={
                    batches?.data?.map((batch) => ({
                      value: batch.id,
                      label: batch.name || `Batch ${batch.id}`,
                    })) || []
                  }
                  error={lessonForm.errors.batchID}
                />

                <DateTimePicker
                  mt={15}
                  label="Lesson Date"
                  placeholder="Select lesson date"
                  {...lessonForm.getInputProps("initialRunTime")}
                  // @ts-ignore
                  timePickerProps={{
                    withDropdown: true,
                    popoverProps: { withinPortal: false },
                    format: "12h",
                  }}
                />

                <Group justify="space-between" mt={30}>
                  <Button
                    variant="outline"
                    color="gray"
                    onClick={() => {
                      setModalFormAction("view");
                      lessonForm.reset();
                    }}
                    leftSection={<IconX size={16} />}
                  >
                    Cancel
                  </Button>
                  <Button
                    color={Colors.PurplePrimary}
                    onClick={UpdateLesson}
                    loading={isCreatingOrUpdatingLesson}
                    leftSection={<IconCheck size={16} />}
                  >
                    Save Changes
                  </Button>
                </Group>
              </div>
            </div>
          )}
          {modalFormAction === "join" && (
            <div className="flex-col">
              <div className="lesson-details-header">
                <Group justify="space-between" mb={20}>
                  <div className="flex-row align-center">
                    <IconNotebook size={24} color={Colors.PurplePrimary} />
                    <Text fw={600} fz={20} ml={10} c={Colors.PurplePrimary}>
                      {currentLesson?.title}
                    </Text>
                  </div>
                  <Badge color={Colors.PurplePrimary} variant="light" size="lg">
                    Created{" "}
                    {dayjs(currentLesson?.createdAt).format("DD MMM YYYY")}
                  </Badge>
                </Group>
              </div>
              <Divider w="100%" mb={10} />
              <Text fw={500}>Lesson Batch</Text>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Batch</Table.Th>
                    <Table.Th>Teacher</Table.Th>
                    <Table.Th>Student Count</Table.Th>
                    <Table.Th>Action</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {batches?.data
                    ?.filter((b) => b.id === currentLesson?.batchID)
                    .map((b) => {
                    const Join = async () => {
                      setIsJoiningLesson(true);
                      const r = await PerformRequest({
                        method: Endpoints.GetJoinLessonRoom(
                          currentLesson?.id as string,
                          b.id
                        ).method,
                        route: Endpoints.GetJoinLessonRoom(
                          currentLesson?.id as string,
                          b.id
                        ).route,
                        callback: () => {
                          setIsJoiningLesson(false);
                        },
                      });
                      setIsJoiningLesson(false);
                      if (r) {
                        if (r.status === HttpSuccessCodesObj.Success) {
                          navigate(`/d/room/${currentLesson?.id}/${b.id}`);
                          DefaultSuccessNotification(
                            "You are now joining the lesson"
                          );
                        } else {
                          DefaultErrorNotification(
                            "Could not join lesson. Please try again."
                          );
                        }
                      }
                    };
                    return (
                      <Table.Tr key={b.id}>
                        <Table.Td>{b.name}</Table.Td>
                        <Table.Td>{getTeacherByID(b.teacherID)?.name}</Table.Td>
                        <Table.Td>{b?.students?.length}</Table.Td>
                        <Table.Td>
                          <Button
                            variant="subtle"
                            onClick={Join}
                            color={Colors.PurplePrimary}
                            loading={isJoiningLesson}
                            leftSection={<IconVideo size={17} />}
                          >
                            Join
                          </Button>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </div>
          )}
        </Modal>
        <Modal
          centered
          size="lg"
          title={
            <Group gap={8}>
              <IconCalendar size={20} />
              <Text fw={600}>Batch Details</Text>
            </Group>
          }
          opened={isBatchModalOpen}
          onClose={handleCloseBatchModal}
        >
          {selectedBatch && (
            <div className="flex-col">
              <div className="batch-details-header">
                <Group justify="space-between" mb={20}>
                  <div className="flex-row align-center">
                    <IconCalendar size={24} color={Colors.PurplePrimary} />
                    <Text fw={600} fz={20} ml={10} c={Colors.PurplePrimary}>
                      {selectedBatch.name ||
                        `Batch ${
                          batches?.data ? batches.data.indexOf(selectedBatch) + 1 : 1
                        }`}
                    </Text>
                  </div>
                  <Badge color={Colors.PurplePrimary} variant="light" size="lg">
                    Created{" "}
                    {dayjs(selectedBatch.createdAt).format("DD MMM YYYY")}
                  </Badge>
                </Group>
              </div>

              <Divider my="md" />

              <div className="batch-details-content">
                <div className="batch-metadata">
                  <Card withBorder p="md" radius="md" bg="gray.0">
                    <Group mb={15}>
                      <IconUsers size={20} color={Colors.PurplePrimary} />
                      <Text fw={500} fz={16} c={Colors.PurplePrimary}>
                        Students in Batch
                      </Text>
                    </Group>
                    <Group gap="xl">
                      <div className="flex-col">
                        <Text fz={12} c="dimmed" mb={5}>
                          Total Students
                        </Text>
                        <Text fw={500} fz={14}>
                          {selectedBatch?.studentIDs?.length}/
                          {selectedBatch?.maxStudents || 5}
                        </Text>
                      </div>
                      <div className="flex-col">
                        <Text fz={12} c="dimmed" mb={5}>
                          Max Students
                        </Text>
                        <Text fw={500} fz={14}>
                          {selectedBatch?.maxStudents || 5}
                        </Text>
                      </div>
                      <div className="flex-col">
                        <Text fz={12} c="dimmed" mb={5}>
                          Current Teacher
                        </Text>
                        <Text fw={500} fz={14}>
                          {getTeacherByID(selectedBatch.teacherID)?.name}
                        </Text>
                      </div>
                    </Group>
                  </Card>
                </div>

                <div className="students-list mt={20}">
                  <Text fw={500} fz={16} mb={10} c={Colors.GrayPrimary}>
                    Students In Batch
                  </Text>
                  <Card withBorder p="md" radius="md" bg="gray.0">
                    {selectedBatch?.studentIDs?.length > 0 ? (
                      <div className="flex-col">
                        {selectedBatch?.students?.map((student, index) => (
                          <div
                            key={student.id}
                            className="flex-row align-center mb={8}"
                          >
                            <Group justify="space-between" w="100%">
                              <div className="flex-row align-center">
                                <Badge
                                  color="blue"
                                  variant="light"
                                  size="sm"
                                  mr={8}
                                >
                                  {index + 1}
                                </Badge>
                                <Text fz={14} c={Colors.GrayPrimary}>
                                  {student.name}
                                </Text>
                              </div>
                              <Button
                                variant="subtle"
                                size="xs"
                                color={Colors.PurplePrimary}
                                onClick={() => OpenTransferStudentModal(student)}
                              >
                                Transfer
                              </Button>
                            </Group>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <Text fz={14} c="dimmed" ta="center">
                        No students assigned to this batch
                      </Text>
                    )}
                  </Card>
                </div>

                <div className="teacher-assignment mt={20}">
                  <Text fw={500} fz={16} mb={10} mt={10} c={Colors.GrayPrimary}>
                    Batch Settings
                  </Text>
                  <TextInput
                    label="Batch Name"
                    value={batchName}
                    onChange={(event) => setBatchName(event.currentTarget.value)}
                    mb={12}
                  />
                  <NumberInput
                    label="Max Students"
                    min={1}
                    value={batchMaxStudents}
                    onChange={(value) =>
                      setBatchMaxStudents(Number(value) || 1)
                    }
                    mb={12}
                  />
                  <Select
                    label="Assign Teacher"
                    placeholder="Select a teacher"
                    data={teachers?.data?.map((teacher) => ({
                      value: teacher.id,
                      label: teacher.name,
                      disabled:
                        (batches?.data?.filter(
                          (b) =>
                            b?.teacherID === teacher?.id &&
                            b?.id !== selectedBatch?.id
                        )?.length ?? [].length) > 0,
                    }))}
                    value={batchTeacher}
                    onChange={(value) => setBatchTeacher(value)}
                    allowDeselect={false}
                    radius="md"
                  />
                </div>
                <Button
                  variant="outline"
                  size="xs"
                  my={10}
                  color={Colors.RedPrimary}
                  onClick={RemoveBatchTeacher}
                  disabled={!selectedBatch?.teacherID}
                  loading={isRemovingBatchTeacher}
                >
                  Remove
                </Button>
                <br />
                <div className="flex-row align-center justify-between width-100">
                  <Button
                    disabled={isRemovingBatchTeacher}
                    variant="outline"
                    color="gray"
                    onClick={handleCloseBatchModal}
                    w="48%"
                    leftSection={<IconX size={16} />}
                  >
                    Close
                  </Button>
                  <Button
                    disabled={isRemovingBatchTeacher}
                    color={Colors.PurplePrimary}
                    onClick={UpdateBatchTeacher}
                    w="48%"
                    loading={isUpdatingBatchTeacher}
                    leftSection={<IconCheck size={16} />}
                  >
                    Save Batch
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Modal>
        <Modal
          centered
          title="Create Batch"
          opened={isCreateBatchModalOpen}
          onClose={() => {
            if (!isCreatingBatch) {
              setIsCreateBatchModalOpen(false);
              createBatchForm.reset();
              createBatchForm.setFieldValue("maxStudents", 5);
            }
          }}
        >
          <div className="flex-col">
            <TextInput
              label="Batch Name"
              placeholder="Leave blank to auto-generate"
              value={createBatchForm.values.name}
              onChange={(event) =>
                createBatchForm.setFieldValue("name", event.currentTarget.value)
              }
              mb={12}
            />
            <NumberInput
              label="Max Students"
              min={1}
              value={createBatchForm.values.maxStudents}
              onChange={(value) =>
                createBatchForm.setFieldValue(
                  "maxStudents",
                  Number(value) || 1
                )
              }
            />
            <Select
              mt={15}
              label="Teacher"
              placeholder="Select a teacher"
              data={
                teachers?.data?.map((teacher) => ({
                  value: teacher.id,
                  label: teacher.name,
                })) || []
              }
              value={createBatchForm.values.teacherID}
              onChange={(value) =>
                createBatchForm.setFieldValue("teacherID", value || "")
              }
            />
            <Button
              mt={20}
              color={Colors.PurplePrimary}
              loading={isCreatingBatch}
              onClick={CreateBatch}
            >
              Create Batch
            </Button>
          </div>
        </Modal>
        <Modal
          centered
          title="Transfer Student"
          opened={isTransferStudentModalOpen}
          onClose={() => {
            if (!isTransferringStudent) {
              setIsTransferStudentModalOpen(false);
              setStudentToTransfer(null);
              setTargetBatchID(null);
            }
          }}
        >
          <div className="flex-col">
            <Text mb={10}>
              Transfer <strong>{studentToTransfer?.name}</strong> to another
              batch.
            </Text>
            <Select
              label="Target Batch"
              placeholder="Select target batch"
              data={
                batches?.data
                  ?.filter(
                    (batch) =>
                      batch.id !== selectedBatch?.id &&
                      batch.studentIDs.length < (batch.maxStudents || 5)
                  )
                  .map((batch, index) => ({
                    value: batch.id,
                    label: `${batch.name || `Batch ${index + 1}`} (${
                      batch.studentIDs.length
                    }/${batch.maxStudents || 5})`,
                  })) || []
              }
              value={targetBatchID}
              onChange={setTargetBatchID}
            />
            <Button
              mt={20}
              color={Colors.PurplePrimary}
              onClick={TransferStudent}
              loading={isTransferringStudent}
              disabled={!targetBatchID}
            >
              Transfer Student
            </Button>
          </div>
        </Modal>
        <Modal
          centered
          size="lg"
          title={
            <Group gap={8}>
              <IconVideo size={20} />
              <Text fw={600}>Create New Recording</Text>
            </Group>
          }
          opened={isRecordingModalOpen}
          onClose={handleCloseRecordingModal}
        >
          <div className="flex-col">
            <div className="recording-form-header">
              <Group justify="space-between" mb={20}>
                <div className="flex-row align-center">
                  <IconVideo size={24} color={Colors.PurplePrimary} />
                  <Text fw={600} fz={20} ml={10} c={Colors.PurplePrimary}>
                    Upload Recording Files
                  </Text>
                </div>
              </Group>
            </div>

            <Divider my="md" />

            <div className="recording-form-content">
              <div className="lesson-selection">
                <Text fw={500} fz={16} mb={10} c={Colors.GrayPrimary}>
                  Select Lesson
                </Text>
                <Select
                  placeholder="Choose a lesson for this recording"
                  data={lessons?.data?.map((lesson) => ({
                    value: lesson.id,
                    label: lesson.title,
                  }))}
                  value={selectedLessonForRecording}
                  onChange={(value) => setSelectedLessonForRecording(value)}
                  clearable
                  radius="md"
                  mb={20}
                />
              </div>

              <div className="file-upload">
                <Text fw={500} fz={16} mb={10} c={Colors.GrayPrimary}>
                  Upload Recording Files
                </Text>
                <Dropzone
                  onDrop={handleRecordingFileDrop}
                  onReject={handleRecordingFileReject}
                  accept={[MIME_TYPES.mp4]}
                  maxFiles={5}
                  maxSize={1024 * 1024 * 1024}
                  radius="md"
                  style={{
                    border: "2px dashed #dee2e6",
                    background: "#f8f9fa",
                  }}
                >
                  <Group
                    justify="center"
                    gap="xl"
                    style={{ minHeight: 80, pointerEvents: "none" }}
                  >
                    <Dropzone.Accept>
                      <IconUpload
                        style={{
                          width: 32,
                          height: 32,
                          color: "var(--mantine-color-blue-6)",
                        }}
                        stroke={1.5}
                      />
                    </Dropzone.Accept>
                    <Dropzone.Reject>
                      <IconX
                        style={{
                          width: 32,
                          height: 32,
                          color: "var(--mantine-color-red-6)",
                        }}
                        stroke={1.5}
                      />
                    </Dropzone.Reject>
                    <Dropzone.Idle>
                      <IconVideo
                        style={{
                          width: 32,
                          height: 32,
                          color: "var(--mantine-color-dimmed)",
                        }}
                        stroke={1.5}
                      />
                    </Dropzone.Idle>
                    <div>
                      <Text size="xl" inline>
                        Drag recording files here or click to select
                      </Text>
                      <Text size="sm" c="dimmed" inline mt={7}>
                        Upload <strong>MP4</strong> files ( 1GB max)
                      </Text>
                    </div>
                  </Group>
                </Dropzone>

                {fileRejectionErrors.length > 0 && (
                  <div className="file-errors mt={10}">
                    <Card
                      withBorder
                      p="md"
                      radius="md"
                      bg="red.0"
                      style={{ borderColor: "var(--mantine-color-red-3)" }}
                    >
                      <Group mb={10}>
                        <IconX size={16} color="var(--mantine-color-red-6)" />
                        <Text fw={500} fz={14} c="red">
                          File Upload Errors
                        </Text>
                      </Group>
                      <div className="flex-col">
                        {fileRejectionErrors.map((error, index) => (
                          <Text key={index} fz={13} c="red" mb={5}>
                            • {error}
                          </Text>
                        ))}
                      </div>
                    </Card>
                  </div>
                )}
              </div>

              {selectedRecordingFiles?.length > 0 && (
                <div className="selected-files mt={20}">
                  <Text fw={500} fz={16} mb={10} c={Colors.GrayPrimary}>
                    Selected Files
                  </Text>
                  <Card withBorder p="md" radius="md" bg="gray.0">
                    <div className="flex-col">
                      {selectedRecordingFiles?.map((file, index) => (
                        <div
                          key={index}
                          className="flex-row align-center mb={8}"
                        >
                          <Badge color="green" variant="light" size="sm" mr={8}>
                            {index + 1}
                          </Badge>
                          <Text fz={14} c={Colors.GrayPrimary}>
                            {file.name} ({(file.size / 1024 / 1024).toFixed(2)}{" "}
                            MB)
                          </Text>
                        </div>
                      ))}
                    </div>
                  </Card>
                </div>
              )}
              <br />
              <div className="flex-row align-center justify-between width-100">
                <Button
                  w="48%"
                  variant="outline"
                  color="gray"
                  onClick={handleCloseRecordingModal}
                  leftSection={<IconX size={16} />}
                >
                  Cancel
                </Button>
                <Button
                  w="48%"
                  color={Colors.PurplePrimary}
                  onClick={CreateNewRecording}
                  loading={isCreatingRecording}
                  disabled={
                    !selectedLessonForRecording ||
                    selectedRecordingFiles.length === 0
                  }
                  leftSection={<IconCheck size={16} />}
                >
                  Create Recording
                </Button>
              </div>
            </div>
          </div>
        </Modal>
        <Modal
          centered
          size="lg"
          title={
            <Group gap={8}>
              <IconVideo size={20} />
              <Text fw={600}>Recording Details</Text>
            </Group>
          }
          opened={isRecordingDetailsModalOpen}
          onClose={handleCloseRecordingDetailsModal}
        >
          {selectedRecording && (
            <div className="flex-col">
              <div className="recording-details-header">
                <Group justify="space-between" mb={20}>
                  <div className="flex-row align-center">
                    <IconVideo size={24} color={Colors.PurplePrimary} />
                    <Text fw={600} fz={20} ml={10} c={Colors.PurplePrimary}>
                      Recording Details
                    </Text>
                  </div>
                  <Badge color={Colors.PurplePrimary} variant="light" size="lg">
                    Created{" "}
                    {dayjs(selectedRecording.createdAt).format("DD MMM YYYY")}
                  </Badge>
                </Group>
              </div>

              <Divider my="md" />

              <div className="recording-details-content">
                <div className="recording-metadata">
                  <Card withBorder p="md" radius="md" bg="gray.0">
                    <Group mb={15}>
                      <IconVideo size={20} color={Colors.PurplePrimary} />
                      <Text fw={500} fz={16} c={Colors.PurplePrimary}>
                        Recording Details
                      </Text>
                    </Group>
                    <Group gap="xl">
                      <div className="flex-col">
                        <Text fz={12} c="dimmed" mb={5}>
                          Lesson
                        </Text>
                        <Text fw={500} fz={14}>
                          {
                            lessons?.data.find(
                              (lesson) =>
                                lesson.id === selectedRecording.lessonID
                            )?.title
                          }
                        </Text>
                      </div>
                      <div className="flex-col">
                        <Text fz={12} c="dimmed" mb={5}>
                          Creation Date
                        </Text>
                        <Text fw={500} fz={14}>
                          {dayjs(selectedRecording.createdAt).format(
                            "DD MMM YYYY"
                          )}
                        </Text>
                      </div>
                      <div className="flex-col">
                        <Text fz={12} c="dimmed" mb={5}>
                          Last Updated
                        </Text>
                        <Text fw={500} fz={14}>
                          {dayjs(selectedRecording.lastUpdatedAt).format(
                            "DD MMM YYYY, hh:mm A"
                          )}
                        </Text>
                      </div>
                    </Group>
                  </Card>
                </div>

                <div className="recording-files mt={20}">
                  <Text fw={500} fz={16} mb={10} c={Colors.GrayPrimary}>
                    Files
                  </Text>
                  <Card withBorder p="md" radius="md" bg="gray.0">
                    <div className="flex-col">
                      {selectedRecording.files.map((filePath, index) => (
                        <div
                          key={index}
                          className="flex-row align-center mb={8}"
                        >
                          <Badge color="blue" variant="light" size="sm" mr={8}>
                            {index + 1}
                          </Badge>
                          <Text fz={14} c={Colors.GrayPrimary}>
                            {filePath.split("/").pop() || filePath}
                          </Text>
                          <ActionIcon
                            size="sm"
                            color="blue"
                            ml={10}
                            onClick={() => handleFileClick(filePath)}
                            variant="subtle"
                          >
                            <IconPhoto size={16} />
                          </ActionIcon>
                        </div>
                      ))}
                    </div>
                  </Card>
                </div>

                <Group justify="space-between" mt={30}>
                  <Button
                    variant="outline"
                    color="gray"
                    onClick={handleCloseRecordingDetailsModal}
                    leftSection={<IconX size={16} />}
                  >
                    Close
                  </Button>
                </Group>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </Container>
  );
};

export default SingleCourse;
